import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import API from "../../services/api";
import { generatePINumber } from "../../utils/autogen";
import COMPANY from "../../config/companyInfo";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const PRODUCTS_API = `${process.env.REACT_APP_API_URL}/api/products`;
const PI_API       = `${process.env.REACT_APP_API_URL}/api/pi`;

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const compute = (base) => {
  const len = parseFloat(base.lengthInMtrs) || 0;
  const wpm = parseFloat(base.weightPerMeter) || 0;
  const qty = parseFloat(base.quantityInNumbers) || 0;
  const ppk = parseFloat(base.pricePerKg) || 0;
  const weightPerLength = parseFloat((len * wpm).toFixed(3));
  const quantityInMeters = parseFloat((len * qty).toFixed(2));
  const totalKgs = parseFloat((weightPerLength * qty).toFixed(2));
  const amount = parseFloat((totalKgs * ppk).toFixed(2));
  return { ...base, weightPerLength, quantityInMeters, totalKgs, amount };
};
const f2 = (n) => Number(n || 0).toFixed(2);
const f3 = (n) => Number(n || 0).toFixed(3);
const fIN = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Empty form — matches QuotationApp's EMPTY_FORM shape, adds productDbId for API products
const EMPTY_FORM = {
  _id: null,
  productDbId: "",                     // DB _id of selected product
  itemCode: "", sectionName: "", sectionImage: "",
  item: "", profileCode: "", hsnCode: "", uom: "KGS",
  thkn: "", weightPerMeter: "", lengthInMtrs: "",
  quantityInNumbers: "", pricePerKg: "",
  weightPerLength: 0, quantityInMeters: 0, totalKgs: 0, amount: 0,
};

// ─── MINIMAL GLOBAL CSS — exact copy from QuotationApp ────────────────────────
const MINIMAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
*,*::before,*::after { box-sizing:border-box; margin:0; padding:0; }
body { background:#ffffff; color:#1a1a2e; font-family:'Poppins',sans-serif; }
@keyframes tup    { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }
@keyframes fadein { from{opacity:0;} to{opacity:1;} }
@keyframes spin   { to{transform:rotate(360deg);} }
.anim-tup    { animation: tup    .2s ease; }
.anim-fadein { animation: fadein .18s ease; }
.anim-spin   { animation: spin   .7s linear infinite; }
`;

// ─── DESIGN TOKENS — exact copy from QuotationApp ─────────────────────────────
const INPUT_CLS = [
  "w-full bg-[#ffffff] border border-[#d0d0e8] rounded-[6px] px-[11px] py-2",
  "text-[#1a1a2e] text-[12px] outline-none",
  "placeholder-[#4a4a75]",
  "transition-all duration-150",
  "focus:border-[#7c6dfa] focus:shadow-[0_0_0_2px_rgba(124,109,250,0.13)]",
  "disabled:opacity-40 disabled:cursor-not-allowed",
].join(" ");

const LABEL_CLS = "text-[9.5px] tracking-[1.5px] uppercase text-[#4a4a75]";

const BTN_BASE = "inline-flex items-center gap-[7px] px-5 py-[9px] rounded-[7px] border-0 text-[13px] font-semibold tracking-[0.5px] cursor-pointer transition-all duration-150";
const BTN_PRI = "bg-[#7c6dfa] text-white hover:bg-[#9080ff] hover:-translate-y-px hover:shadow-[0_4px_14px_rgba(124,109,250,0.35)]";
const BTN_AMBER = "bg-[#f0c040] text-[#ffffff] hover:bg-[#f8d060] hover:-translate-y-px";
const BTN_GREEN = "bg-[#40d098] text-[#ffffff] hover:bg-[#55e0aa] hover:-translate-y-px";
const BTN_OUT = "bg-transparent border border-[#b8b8d4] text-[#1a1a2e] hover:border-[#7c6dfa] hover:text-white";
const BTN_EDIT = "inline-flex items-center bg-transparent border border-[rgba(124,109,250,0.3)] text-[#7c6dfa] px-3 py-[5px] text-[11px] font-semibold rounded-[7px] cursor-pointer transition-all hover:bg-[rgba(124,109,250,0.1)]";
const BTN_DEL = "inline-flex items-center bg-transparent border border-[rgba(240,80,96,0.3)]  text-[#f05060] px-3 py-[5px] text-[11px] font-semibold rounded-[7px] cursor-pointer transition-all hover:bg-[rgba(240,80,96,0.1)]";

const col2 = { gridColumn: "span 2" };
const col3 = { gridColumn: "span 3" };

// ─── PANEL — exact copy from QuotationApp ─────────────────────────────────────
const Panel = ({ children }) => (
  <div className="relative bg-[#f8f8ff] border border-[#d0d0e8] rounded-xl overflow-hidden mb-[22px]">
    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#7c6dfa] via-[#40c0f0] to-[#f0c040]" />
    {children}
  </div>
);

const PanelHead = ({ left, right }) => (
  <div className="px-[22px] py-[14px] border-b border-[#d0d0e8] flex items-center justify-between">
    <span className="text-[11px] font-semibold tracking-[3px] uppercase text-[#4a4a75]" style={{ fontFamily: "'Poppins',sans-serif" }}>{left}</span>
    {right}
  </div>
);

// ─── FORM GRID — exact copy ───────────────────────────────────────────────────
const FGrid = ({ children, className = "" }) => (
  <div
    className={className}
    style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(182px,1fr))", gap: "13px" }}
  >
    {children}
  </div>
);

// ─── FIELD GROUP — exact copy ─────────────────────────────────────────────────
const FGrp = ({ label, required, auto, children, style }) => (
  <div className="flex flex-col gap-[5px]" style={style}>
    <label
      className={LABEL_CLS}
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      {label}

      {label === "GSTIN" && (
        <Link
          to="https://cleartax.in/gst-number-search/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#1ea01e] hover:underline ml-[10px] text-[9px]"
        >
          Link
        </Link>
      )}

      {required && (
        <span className="text-[#f0c040] not-italic ml-[2px]">*</span>
      )}

      {auto && (
        <span className="text-[#40d098] text-[9px] ml-1">● auto</span>
      )}
    </label>

    {children}
  </div>
);

// ─── COMPUTED DISPLAYS — exact copy ──────────────────────────────────────────
const Computed = ({ children }) => (
  <div
    className="bg-[rgba(64,208,152,0.07)] border border-[rgba(64,208,152,0.17)] rounded-[6px] px-[11px] py-2 text-[#40d098] text-[12px] min-h-[36px] flex items-center"
    style={{ fontFamily: "'Poppins', sans-serif" }}
  >{children}</div>
);
const ComputedGold = ({ children }) => (
  <div
    className="bg-[rgba(240,192,64,0.07)] border border-[rgba(240,192,64,0.2)] rounded-[6px] px-[11px] py-2 text-[#f0c040] text-[12px] min-h-[36px] flex items-center"
    style={{ fontFamily: "'Poppins', sans-serif" }}
  >{children}</div>
);

// ─── SECTION LABEL — exact copy ──────────────────────────────────────────────
const SectionLabel = ({ children, divider }) => (
  <p
    className={`text-[9.5px] tracking-[1.5px] uppercase text-[#4a4a75] mb-[14px] ${divider ? "border-t border-[#d0d0e8] pt-[14px]" : ""}`}
    style={{ fontFamily: "'Poppins', sans-serif" }}
  >
    {children}
  </p>
);

// ─── TOAST HOOK — exact copy from QuotationApp ───────────────────────────────
let _tid = 0;
function useToast() {
  const [list, setList] = useState([]);
  const toast = useCallback((msg, type = "ok") => {
    const id = ++_tid;
    setList(t => [...t, { id, msg, type }]);
    setTimeout(() => setList(t => t.filter(x => x.id !== id)), 3000);
  }, []);
  return { list, toast };
}

// ─── NUM TO WORDS (Indian system) ────────────────────────────────────────────
function numToWords(amount) {
  const o = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
  const t = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  const w = (n) => {
    if (!n) return "";
    if (n < 20)  return o[n] + " ";
    if (n < 100) return t[Math.floor(n/10)] + " " + (o[n%10] ? o[n%10] + " " : "");
    if (n < 1000) return o[Math.floor(n/100)] + " Hundred " + w(n%100);
    if (n < 1e5)  return w(Math.floor(n/1000)) + "Thousand " + w(n%1000);
    if (n < 1e7)  return w(Math.floor(n/1e5))  + "Lakh "     + w(n%1e5);
    return w(Math.floor(n/1e7)) + "Crore " + w(n%1e7);
  };
  const int = Math.floor(amount), paise = Math.round((amount - int) * 100);
  let r = "Rupees " + w(int).trim().replace(/\s+/g," ");
  if (paise) r += " and " + w(paise).trim().replace(/\s+/g," ") + " Paise";
  return r + " Only";
}

// ─── PDF GENERATION ───────────────────────────────────────────────────────────
function generatePDF(biz, items, toast) {
  if (!biz.companyName || !biz.piRefNo) { toast("Fill Company Name & PI Ref No. first", "err"); return; }
  if (!items.length) { toast("Add at least one item", "err"); return; }
  try {
    // ── Setup ─────────────────────────────────────────────────────────────────
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const PW = 210, PH = 297, ML = 8, MR = 8, MT = 8;
    const CW = PW - ML - MR;

    // Colors
    const NAVY = [13, 31, 92];
    const BLUE = [21, 101, 192];
    const LBLUE = [232, 234, 246];
    const GREEN = [27, 94, 32];
    const WHITE = [255, 255, 255];
    const LGRAY = [245, 246, 250];
    const BORDER = [180, 190, 220];
    const BLACK = [20, 20, 20];
    const GRAY = [110, 110, 120];

    // ── Drawing helpers ────────────────────────────────────────────────────────
    const fillRect = (x, y, w, h, fill, stroke, sw = 0.2) => {
      if (fill) { doc.setFillColor(...fill); doc.rect(x, y, w, h, "F"); }
      if (stroke) { doc.setDrawColor(...stroke); doc.setLineWidth(sw); doc.rect(x, y, w, h, "S"); }
    };
    const fillRectFD = (x, y, w, h, fill, stroke = BORDER, sw = 0.2) => {
      doc.setFillColor(...fill); doc.setDrawColor(...stroke); doc.setLineWidth(sw); doc.rect(x, y, w, h, "FD");
    };
    const txt = (s, x, y, { bold = false, size = 7, color = BLACK, align = "left", maxW = null } = {}) => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(size); doc.setTextColor(...color);
      const opts = { align }; if (maxW) opts.maxWidth = maxW;
      doc.text(String(s ?? ''), x, y, opts);
    };
    const cell = (s, x, y, w, h, {
      fill = null, stroke = BORDER, sw = 0.15,
      bold = false, size = 6.5, color = BLACK,
      align = "left", vAlign = "middle", pl = 1.8, pr = 1.8,
    } = {}) => {
      if (fill) { doc.setFillColor(...fill); doc.rect(x, y, w, h, "F"); }
      if (stroke) { doc.setDrawColor(...stroke); doc.setLineWidth(sw); doc.rect(x, y, w, h, "S"); }
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.setFontSize(size); doc.setTextColor(...color);
      const tw = w - pl - pr;
      const lines = String(s ?? '').split("\n").flatMap(l => doc.splitTextToSize(l, tw));
      const lh = size * 0.42, th = lines.length * lh + (lines.length - 1) * 0.4;
      const ty0 = vAlign === "top" ? y + 2 : y + h / 2 - th / 2 + lh * 0.85;
      const tx = align === "center" ? x + w / 2 : align === "right" ? x + w - pr : x + pl;
      lines.forEach((l, i) => doc.text(l, tx, ty0 + i * (lh + 0.4), { align, maxWidth: tw }));
    };

    // ── Totals ─────────────────────────────────────────────────────────────────
    const totAmt = items.reduce((s, r) => s + (r.amount || 0), 0);
    const totKgs = items.reduce((s, r) => s + (r.totalKgs || 0), 0);
    const loadingC = parseFloat(biz.loadingCharge) || 0;
    const igst = (totAmt + loadingC) * COMPANY.gstRate;
    const grandTot = totAmt + loadingC + igst;
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) : "—";

    let Y = MT;

    // ══════════════════════════════════════════════════════════════════════════
    // 1. HEADER
    // ══════════════════════════════════════════════════════════════════════════
    const HDR_H = 46, LOGO_W = 46, MID_W = 100, QUOT_W = CW - LOGO_W - MID_W;

    doc.setDrawColor(...BORDER); doc.setLineWidth(0.4); doc.rect(ML, Y, CW, HDR_H);

    // // Logo panel
    // fillRect(ML, Y, LOGO_W, HDR_H, LBLUE, BORDER, 0.3);
    // doc.setFillColor(...NAVY); doc.circle(ML+LOGO_W/2, Y+16, 11, "F");
    // doc.setFillColor(...WHITE); doc.circle(ML+LOGO_W/2, Y+16, 8.5, "F");
    // doc.setFillColor(...NAVY); doc.circle(ML+LOGO_W/2, Y+16, 7, "F");
    // // txt("R",            ML+LOGO_W/2, Y+18.5, {bold:true, size:13, color:WHITE, align:"center"});
    // txt("STRENGTH  •  QUALITY  •  TRUST", ML+LOGO_W/2, Y+36, {size:5, color:GRAY, align:"center"});

    // Logo panel


    // Draw logo
    doc.addImage(
      COMPANY.logo,           // Image source
      "PNG",           // Image format
      ML + LOGO_W / 2 - 15,
      Y + 16 - 5,
      30,
      14
    );

    txt(
      COMPANY.tagline,
      ML + LOGO_W / 2,
      Y + 36,
      {
        size: 5,
        color: GRAY,
        align: "center",
      }
    );


    // Middle info panel
    const midX = ML + LOGO_W;
    fillRect(midX, Y, MID_W, HDR_H, WHITE, BORDER, 0.3);
    txt(COMPANY.name, midX + MID_W / 2, Y + 13, { bold: true, size: 20, color: NAVY, align: "center" });
    txt(`GSTIN : ${COMPANY.gstin}`, midX + 4, Y + 20, { bold: true, size: 7.5, color: BLUE });
    doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(...BLACK);
    doc.text(`• Works / Regd. Office : ${COMPANY.addressLine1}`, midX + 4, Y + 25.5);
    doc.text(`  ${COMPANY.addressLine2}`, midX + 4, Y + 29);
    txt(`Tel : ${COMPANY.phone}`, midX + 4, Y + 34, { size: 6.5 });
    txt(`Email : ${COMPANY.email}`, midX + 50, Y + 34, { size: 6.5 });

    // Quotation box
    const quotX = midX + MID_W;
    fillRectFD(quotX, Y, QUOT_W, 12, NAVY, NAVY, 0.3);
    txt("QUOTATION", quotX + QUOT_W / 2, Y + 8.5, { bold: true, size: 11, color: WHITE, align: "center" });
    const qRows = [
      ["PI REF. NO.", biz.piRefNo || ""],
      ["DATE", fmtDate(biz.date)],
      ["ENQUIRY REF. NO.", biz.enquiryRef || ""],
      ["VALIDITY", "15 DAYS"],
    ];
    const qRH = (HDR_H - 12) / qRows.length;
    qRows.forEach(([lbl, val], i) => {
      const ry = Y + 12 + i * qRH;
      fillRectFD(quotX, ry, QUOT_W, qRH, i % 2 === 0 ? LBLUE : WHITE, BORDER, 0.15);
      txt(lbl, quotX + 2.5, ry + 3.5, { bold: true, size: 5.5, color: NAVY });
      txt(val, quotX + 2.5, ry + 7, { size: 6.5, color: BLACK });
    });

    Y += HDR_H + 3;

    // ══════════════════════════════════════════════════════════════════════════
    // 2. BILL TO / SHIP TO / PROFILE
    // ══════════════════════════════════════════════════════════════════════════
    const S2H = 40, billW = Math.floor(CW / 2), compW = Math.floor(CW /2), profW = CW - billW - compW;

    // Bill To
    fillRectFD(ML, Y, billW, 8, NAVY, NAVY, 0.3);
    txt("  BILL TO PARTY", ML + 2, Y + 5.8, { bold: true, size: 6.5, color: WHITE });
    fillRectFD(ML, Y + 8, billW, S2H - 8, LBLUE, BORDER, 0.3);
    let bY = Y + 14;
    doc.splitTextToSize(biz.companyName || "", billW - 6).slice(0, 2).forEach(l => {
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...NAVY);
      doc.text(l, ML + 3, bY); bY += 4.5;
    });
    txt("GSTIN : " + (biz.gstin || "—"), ML + 3, bY, { size: 6.5 }); bY += 4;
    txt("ADDRESS :", ML + 3, bY, { bold: true, size: 6.5 }); bY += 3.5;
    doc.splitTextToSize(biz.address || "", billW - 6).slice(0, 3).forEach(l => {
      txt(l, ML + 3, bY, { size: 6.5 }); bY += 3.2;
    });

    // Ship To
    const compX = ML + billW;
    fillRectFD(compX, Y, compW, 8, BLUE, BLUE, 0.3);
    txt("  COMPANY NAME", compX + 2, Y + 5.8, { bold: true, size: 6.5, color: WHITE });
    fillRectFD(compX, Y + 8, compW, S2H - 8, [240, 244, 255], BORDER, 0.3);
    let cY = Y + 14;
    doc.splitTextToSize(biz.shipCompanyName || biz.companyName || "", compW - 6).slice(0, 2).forEach(l => {
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...NAVY);
      doc.text(l, compX + 3, cY); cY += 4.5;
    });
    txt("PH. NO.", compX + 3, cY, { bold: true, size: 6.5 }); cY += 3.5;
    txt(biz.shipPhone || biz.mobile || "—", compX + 3, cY, { size: 6.5 }); cY += 4;
    txt("ADDRESS :", compX + 3, cY, { bold: true, size: 6.5 }); cY += 3.5;
    doc.splitTextToSize(biz.shipAddress || biz.address || "", compW - 6).slice(0, 3).forEach(l => {
      txt(l, compX + 3, cY, { size: 6.5 }); cY += 3.2;
    });

    // Profile
    // const profX = compX + compW;
    // fillRectFD(profX, Y, profW, S2H, LBLUE, BORDER, 0.3);
    // txt("PROFILE NAME", profX + profW / 2, Y + 7, { bold: true, size: 7, color: NAVY, align: "center" });
    // doc.setDrawColor(...BORDER); doc.setLineWidth(0.2); doc.line(profX + 3, Y + 9, profX + profW - 3, Y + 9);
    // if (biz.profileName) txt(biz.profileName, profX + profW / 2, Y + S2H / 2 + 6, { bold: true, size: 11, color: NAVY, align: "center" });

    Y += S2H + 4;

    // ══════════════════════════════════════════════════════════════════════════
    // 3. DEAR SIR
    // ══════════════════════════════════════════════════════════════════════════
    txt("Dear Sir/Mam,", ML, Y, { size: 7 }); Y += 4;
    doc.splitTextToSize("We are pleased to offer the following items and quantities against your above referred enquiry subjected to the quantity tolerances terms and conditions mentioned below.", CW)
      .forEach(l => { txt(l, ML, Y, { size: 7 }); Y += 3.5; });
    Y += 2;

    // ══════════════════════════════════════════════════════════════════════════
    // 4. ITEMS TABLE
    // ══════════════════════════════════════════════════════════════════════════
    const COLS = [
      { l: "S.NO.", w: 8, a: "center" },
      { l: "ITEM\nCODE", w: 12, a: "center" },
      { l: "SECTION\nNAME", w: 22, a: "left" },
      { l: "SECTION\nDRAWING", w: 16, a: "center" },
      { l: "ITEM / SIZE\n(MM)", w: 22, a: "center" },
      { l: "HSN\nCODE", w: 14, a: "center" },
      { l: "UOM", w: 9, a: "center" },
      { l: "THKN\n(MM)", w: 9, a: "center" },
      { l: "LENGTH\n(MTRS)", w: 12, a: "center" },
      { l: "WEIGHT\nPER MTR\n(KGS)", w: 14, a: "center" },
      { l: "IN MTR", w: 11, a: "center" },
      { l: "TOTAL\nKGS", w: 11, a: "center" },
      { l: "NO. OF\nPCS", w: 10, a: "center" },
      { l: "RATE\nPER KG\n(Rs. )", w: 11, a: "right" },
      { l: "AMOUNT\n(Rs. )", w: 13, a: "right" },
    ];
    let _cx = ML;
    const COL_X = COLS.map(c => { const x = _cx; _cx += c.w; return x; });
    const QUANT_IDX = 10, QUANT_W = COLS[10].w + COLS[11].w + COLS[12].w;
    const TH1 = 9, TH2 = 5, ROW_H = 14;

    const drawHeader = (hy) => {
      COLS.forEach((col, i) => {
        if (i === QUANT_IDX || i === QUANT_IDX + 1 || i === QUANT_IDX + 2) return;
        cell(col.l, COL_X[i], hy, col.w, TH1 + TH2, { fill: NAVY, stroke: WHITE, sw: 0.25, bold: true, size: 5.8, color: WHITE, align: col.a });
      });
      const qx = COL_X[QUANT_IDX];
      cell("QUANTITY", qx, hy, QUANT_W, TH1, { fill: NAVY, stroke: WHITE, sw: 0.25, bold: true, size: 6.5, color: WHITE, align: "center" });
      cell("IN MTR", qx, hy + TH1, COLS[10].w, TH2, { fill: [30, 60, 130], stroke: WHITE, sw: 0.2, bold: true, size: 5.5, color: WHITE, align: "center" });
      cell("TOTAL KGS", qx + COLS[10].w, hy + TH1, COLS[11].w, TH2, { fill: [30, 60, 130], stroke: WHITE, sw: 0.2, bold: true, size: 5.5, color: WHITE, align: "center" });
      cell("PCS", qx + COLS[10].w + COLS[11].w, hy + TH1, COLS[12].w, TH2, { fill: [30, 60, 130], stroke: WHITE, sw: 0.2, bold: true, size: 5.5, color: WHITE, align: "center" });
    };

    drawHeader(Y);
    Y += TH1 + TH2;

    items.forEach((row, idx) => {
      if (Y + ROW_H > PH - 65) { doc.addPage(); Y = MT; drawHeader(Y); Y += TH1 + TH2; }
      const rowFill = idx % 2 === 0 ? WHITE : LGRAY;
      const vals = [
        idx + 1, row.itemCode || "", row.sectionName || "", "",
        row.item || "", row.hsnCode || "", row.uom || "KGS",
        row.thkn || "", f3(row.lengthInMtrs), f3(row.weightPerLength || row.weightPerMeter),
        f2(row.quantityInMeters), f2(row.totalKgs),
        String(row.quantityInNumbers || ""),
        f2(row.pricePerKg), fIN(row.amount),
      ];
      vals.forEach((v, vi) => {
        const isAmt = vi === 14;
        cell(String(v), COL_X[vi], Y, COLS[vi].w, ROW_H, {
          fill: rowFill, stroke: BORDER, sw: 0.1,
          bold: isAmt, size: isAmt ? 7 : 6.5,
          color: isAmt ? NAVY : BLACK, align: COLS[vi].a,
        });
        if (vi === 3 && row.sectionImage) {
          try { doc.addImage(row.sectionImage, "PNG", COL_X[3] + 1, Y + 1, COLS[3].w - 2, ROW_H - 2); } catch (_) { }
        }
      });
      Y += ROW_H;
    });

    // Total row
    const bundleColsW = COLS.slice(0, QUANT_IDX).reduce((s, c) => s + c.w, 0);
    // cell("TOTAL NO. OF BUNDLES :", ML, Y, bundleColsW, 8, {fill:LGRAY, stroke:BORDER, sw:0.15, bold:true, size:6.5, align:"right", pr:3});
    cell("TOTAL KGS", COL_X[QUANT_IDX], Y, QUANT_W, 4, { fill: NAVY, stroke: NAVY, sw: 0.1, bold: true, size: 5.5, color: WHITE, align: "center" });
    cell(f2(totKgs), COL_X[QUANT_IDX], Y + 4, QUANT_W, 4, { fill: LBLUE, stroke: BORDER, sw: 0.1, bold: true, size: 7, color: NAVY, align: "center" });
    cell("", COL_X[13], Y, COLS[13].w + COLS[14].w, 8, { fill: LGRAY, stroke: BORDER, sw: 0.1 });
    Y += 12;

    // ══════════════════════════════════════════════════════════════════════════
    // 5. BANK / QR / TOTALS
    // ══════════════════════════════════════════════════════════════════════════
    if (Y + 52 > PH - 60) { doc.addPage(); Y = MT; }
    const BANK_W = Math.floor(CW * 0.37), QR_W = Math.floor(CW * 0.22), AMT_W = CW - BANK_W - QR_W;
    const SEC_Y = Y;

    // Bank details
    fillRectFD(ML, SEC_Y, BANK_W, 8, NAVY, NAVY, 0.3);
    txt("  OUR BANK DETAILS", ML + 2, SEC_Y + 5.8, { bold: true, size: 7, color: WHITE });
    fillRectFD(ML, SEC_Y + 8, BANK_W, 44, WHITE, BORDER, 0.3);
    const bkRows = [["BANK NAME", COMPANY.bank.name], ["A/C NO.", COMPANY.bank.accountNo], ["IFSC CODE", COMPANY.bank.ifsc], ["BRANCH", COMPANY.bank.branch], ["ADDRESS", COMPANY.bank.address]];
    let bkY = SEC_Y + 13;
    bkRows.forEach(([k, v]) => {
      txt(k + " :", ML + 3, bkY, { bold: true, size: 6 }); txt(v, ML + 32, bkY, { size: 6 }); bkY += 6;
    });

    // QR / Scan & Pay
    const qrX = ML + BANK_W;
    fillRectFD(qrX, SEC_Y, QR_W, 52, [245, 248, 255], BORDER, 0.3);
    txt("SCAN & PAY", qrX + QR_W / 2, SEC_Y + 6.5, { bold: true, size: 7.5, color: NAVY, align: "center" });
    try { 
      // doc.addImage("/payment-qr.png", "PNG", qrX + 3, SEC_Y + 9, QR_W - 6, QR_W - 6); 
    } catch (_) { }
    txt("Thank you for your", qrX + QR_W / 2, SEC_Y + 43, { size: 5.5, color: GRAY, align: "center" });
    txt("valuable enquiry.", qrX + QR_W / 2, SEC_Y + 47, { size: 5.5, color: GRAY, align: "center" });

    // Financial summary
    const amtX = qrX + QR_W;
    const amtRows = [["TOTAL ASSESSABLE VALUE", fIN(totAmt)], ["LOADING CHARGE", fIN(loadingC)], [`IGST ${COMPANY.gstRate * 100}%`, fIN(igst)]];
    const aRH = 9;
    amtRows.forEach(([lbl, val], i) => {
      fillRectFD(amtX, SEC_Y + i * aRH, AMT_W, aRH, WHITE, BORDER, 0.15);
      txt(lbl, amtX + 3, SEC_Y + i * aRH + 5.8, { bold: true, size: 6.5 });
      txt("Rs. " + val, amtX + AMT_W - 3, SEC_Y + i * aRH + 5.8, { size: 6.5, align: "right" });
    });
    // Grand Total
    const gY = SEC_Y + amtRows.length * aRH, gH = 52 - amtRows.length * aRH;
    fillRectFD(amtX, gY, AMT_W, gH, GREEN, GREEN, 0.3);
    txt("GRAND TOTAL", amtX + 3, gY + 9, { bold: true, size: 9, color: WHITE });
    txt("Rs. " + fIN(grandTot) + " /-", amtX + AMT_W - 3, gY + 9, { bold: true, size: 11, color: WHITE, align: "right" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(5.5); doc.setTextColor(...WHITE);
    doc.splitTextToSize("(" + numToWords(grandTot) + ")", AMT_W - 6).forEach((l, i) => {
      doc.text(l, amtX + 3, gY + 15 + i * 3.5);
    });

    Y = SEC_Y + 52 + 4;

    // ══════════════════════════════════════════════════════════════════════════
    // 6. SPECS / TERMS / SIGNATURE
    // ══════════════════════════════════════════════════════════════════════════
    if (Y + 58 > PH - 14) { doc.addPage(); Y = MT; }
    const BOT_H = 58, specW = Math.floor(CW * 0.33), termW = Math.floor(CW * 0.35), sigW = CW - specW - termW;

    // Material Specs
    fillRectFD(ML, Y, specW, BOT_H, WHITE, BORDER, 0.3);
    const specItems = [
      ["MATERIAL SPECIFICATIONS", biz.materialSpec || "PRE-GALVANIZED STEEL CONFORMING TO IS277 MAX-120GSM"],
      ["PAYMENT TERMS", biz.paymentTerms || ""],
      ["DELIVERY TERMS", biz.deliveryTerms || ""],
      ["PACKING", biz.packing || "AS PER OUR STANDARDS"],
      ["INSPECTION", biz.inspection || "IF REQD AT OUR WORKS BY YOUR REPRESENTATIVE PRIOR TO DISPATCH"],
    ];
    let spY = Y + 4;
    specItems.forEach(([lbl, val]) => {
      txt(lbl, ML + 3, spY, { bold: true, size: 6, color: NAVY }); spY += 3.5;
      doc.splitTextToSize(val, specW - 6).slice(0, 3).forEach(l => {
        txt(l, ML + 3, spY, { size: 5.8 }); spY += 3;
      });
      spY += 1;
    });

    // Terms & Conditions
    const termX = ML + specW;
    fillRectFD(termX, Y, termW, BOT_H, WHITE, BORDER, 0.3);
    txt("TERMS & CONDITIONS", termX + termW / 2, Y + 6, { bold: true, size: 7.5, color: NAVY, align: "center" });
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.2); doc.line(termX + 3, Y + 8.5, termX + termW - 3, Y + 8.5);
    const tcList = [
      "This quotation is valid for 15 days from the date of issue.",
      "Prices are subject to change without prior notice.",
      "Goods once sold will not be taken back.",
      "Disputes, if any, will be subjected to jurisdiction of Balaghat court only.",
    ];
    let tcY = Y + 13;
    tcList.forEach(tc => {
      doc.setFillColor(...GREEN); doc.circle(termX + 5, tcY - 1.5, 2, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(...WHITE);
      doc.text(">", termX + 5, tcY - 0.8, { align: "center" });
      doc.splitTextToSize(tc, termW - 14).forEach((l, i) => {
        txt(l, termX + 10, tcY + i * 3.2, { size: 6 });
      });
      tcY += doc.splitTextToSize(tc, termW - 14).length * 3.2 + 3;
    });
    // txt("★  ★  ★", termX+termW/2, Y+BOT_H-4, {size:8, color:NAVY, align:"center"});

    // Signature
    const sigX = termX + termW;
    fillRectFD(sigX, Y, sigW, BOT_H, WHITE, BORDER, 0.3);
    txt(`FOR : ${COMPANY.name}`, sigX + 3, Y + 7, { bold: true, size: 7, color: NAVY });
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.2); doc.line(sigX + 2, Y + 9, sigX + sigW - 2, Y + 9);
    txt("PREPARED BY", sigX + 3, Y + 15, { size: 5.8, color: GRAY });
    txt(biz.preparedBy || COMPANY.preparedByDefault, sigX + 3, Y + 19, { bold: true, size: 7 });
    txt("AUTHORIZED SIGNATORY", sigX + 3, Y + 28, { size: 5.8, color: GRAY });
    doc.setDrawColor(...BORDER); doc.setLineWidth(0.3); doc.line(sigX + 3, Y + 40, sigX + sigW - 3, Y + 40);
    txt(biz.authorizedBy || COMPANY.authorizedByDefault, sigX + 3, Y + 44, { bold: true, size: 7 });
    txt(COMPANY.signatoryLine1, sigX + 3, Y + 48, { size: 6, color: GRAY });
    txt(COMPANY.signatoryLine2, sigX + 3, Y + 52, { size: 6, color: GRAY });
    try {
      doc.addImage(COMPANY.sealSign, "PNG", sigX + sigW - 60, Y + 16, 28, 28);
    } catch (_) {

    }
    Y += BOT_H + 1;

    // ══════════════════════════════════════════════════════════════════════════
    // 7. FOOTER
    // ══════════════════════════════════════════════════════════════════════════
    const FY = PH - 16;
    if (Y + 2 > FY) { doc.addPage(); }
    fillRectFD(ML, FY, CW, 10, NAVY, NAVY, 0.3);
    txt(COMPANY.phone,                                 ML + 4,   FY + 6.5, { size: 6.5, color: WHITE });
    txt("|",                                           ML + 36,  FY + 6.5, { size: 6.5, color: WHITE });
    txt(COMPANY.email,                                ML + 40,  FY + 6.5, { size: 6.5, color: WHITE });
    txt("|",                                           ML + 96,  FY + 6.5, { size: 6.5, color: WHITE });
    txt(COMPANY.addressShort, ML + 100, FY + 6.5, { size: 6.5, color: WHITE });
    txt(COMPANY.footerNote, PW / 2, PH - 3, { size: 6, color: GRAY, align: "center" });

    doc.save(`RR-Industries-PI-${biz.piRefNo.replace(/\//g, "-")}.pdf`);
    toast("PDF generated successfully!");
  } catch (err) { console.error("PDF error:", err); toast("PDF generation failed: " + err.message, "err"); }
}

// ─── BIZ FORM — same layout as QuotationApp, pre-filled from client prop ─────
// Module-scope so React doesn't remount it mid-session
function BizForm({ biz, setBiz, onNext, client }) {
  const h = (f, v) => setBiz(b => ({ ...b, [f]: v }));
  const inp = (field, placeholder = "", type = "text") => (
    <input
      className={INPUT_CLS}
      style={{ fontFamily: "'Poppins', sans-serif" }}
      type={type}
      value={biz[field]}
      onChange={e => h(field, e.target.value)}
      placeholder={placeholder}
    />
  );

  return (
    <Panel>
      <PanelHead
        left="PI Details — Client Information"
        right={
          client && (
            <span className="text-[10px] text-[#40d098] bg-[rgba(64,208,152,0.08)] border border-[rgba(64,208,152,0.2)] px-[10px] py-[3px] rounded-full" style={{ fontFamily: "'Poppins', sans-serif" }}>
              ✓ pre-filled · {client.companyName}
            </span>
          )
        }
      />
      <div className="px-[22px] py-5">

        <SectionLabel>Document Reference</SectionLabel>
        <FGrid className="mb-[18px]">
          <FGrp label="PI Ref No." required>
            <div className="flex gap-[6px]">
              {inp("piRefNo", "RR/26-XXX-03-XX")}
              <button
                className={`${BTN_BASE} ${BTN_OUT} flex-shrink-0`}
                style={{ fontFamily: "'Poppins',sans-serif", padding: "5px 11px", fontSize: 11 }}
                onClick={() => setBiz(b => ({ ...b, piRefNo: generatePINumber() }))}
                title="Generate new PI number"
              >↺</button>
            </div>
          </FGrp>
          <FGrp label="Date">
            <input className={INPUT_CLS} style={{ fontFamily: "'Poppins', sans-serif" }} type="date" value={biz.date} onChange={e => h("date", e.target.value)} />
          </FGrp>
          <FGrp label="Profile Name">{inp("profileName", "e.g. OKO CRAFT")}</FGrp>
        </FGrid>

        <SectionLabel divider>Bill To Party</SectionLabel>
        <FGrid className="mb-[18px]">
          <FGrp label="Company Name" required style={col2}>{inp("companyName", "Company Name")}</FGrp>
          <FGrp label="GSTIN">{inp("gstin", "22AALCD9302L1ZK")}</FGrp>
          <FGrp label="Mobile">{inp("mobile", "9876543210", "tel")}</FGrp>
          <FGrp label="Full Address" style={col3}>{inp("address", "House No, Area, City, State, PIN")}</FGrp>
        </FGrid>

        <SectionLabel divider>
          Ship To Party <span className="normal-case text-[#4a4a75]">(blank = same as bill to)</span>
        </SectionLabel>
        <FGrid className="mb-[18px]">
          <FGrp label="Ship-to Company Name" style={col2}>{inp("shipCompanyName")}</FGrp>
          <FGrp label="Ship-to Phone">{inp("shipPhone")}</FGrp>
          <FGrp label="Ship-to Address" style={col3}>{inp("shipAddress")}</FGrp>
        </FGrid>

        <SectionLabel divider>Terms & Charges</SectionLabel>
        <FGrid>
          <FGrp label="Enquiry Ref / Phone">{inp("enquiryRef", "PH. NO. 7987779610")}</FGrp>
          <FGrp label="Material Specifications" style={col3}>{inp("materialSpec")}</FGrp>
          <FGrp label="Payment Terms" style={col3}>{inp("paymentTerms")}</FGrp>
          <FGrp label="Delivery Terms" style={col3}>{inp("deliveryTerms")}</FGrp>
          <FGrp label="Packing" style={col2}>{inp("packing")}</FGrp>
          <FGrp label="Inspection" style={col2}>{inp("inspection")}</FGrp>
          <FGrp label="Loading Charge (₹)">
            <input className={INPUT_CLS} style={{ fontFamily: "'Poppins', sans-serif" }} type="number" value={biz.loadingCharge} onChange={e => h("loadingCharge", e.target.value)} />
          </FGrp>
          <FGrp label="Prepared By">{inp("preparedBy")}</FGrp>
          <FGrp label="Authorized By">{inp("authorizedBy")}</FGrp>
        </FGrid>

        <div className="flex gap-[10px] mt-[18px] flex-wrap">
          <button className={`${BTN_BASE} ${BTN_PRI}`} style={{ fontFamily: "'Poppins',sans-serif" }} onClick={onNext}>
            PROCEED TO ITEMS →
          </button>
        </div>
      </div>
    </Panel>
  );
}

// ─── ITEM FORM — same layout as QuotationApp, catalog from API ────────────────
// Module-scope — prevents focus-loss bug
function ItemForm({ editItem, catalog, catalogLoading, onAdd, onUpdate, onBack, toast }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [srch, setSrch] = useState("");
  const [ddOpen, setDdOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const ddRef = useRef(null);
  const live = useMemo(() => compute(form), [form]);

  // Filtered catalog list for dropdown
  const filt = useMemo(() =>
    !srch.trim() ? catalog
      : catalog.filter(p =>
        p.sectionName.toLowerCase().includes(srch.toLowerCase()) ||
        p.itemCode.toLowerCase().includes(srch.toLowerCase()) ||
        (p.item || "").toLowerCase().includes(srch.toLowerCase()) ||
        (p.brand || "").toLowerCase().includes(srch.toLowerCase())
      ),
    [srch, catalog]);

  // Thickness variants of the currently selected product
  const selectedProduct = catalog.find(p => p._id === form.productDbId) || null;
  const thkOptions = selectedProduct?.profileThickness || [];
  const selectedThknIdx = thkOptions.findIndex(t => t.thkn === form.thkn);

  useEffect(() => {
    if (editItem) { setForm({ ...editItem }); setSrch(editItem.sectionName || ""); }
    else { setForm(EMPTY_FORM); setSrch(""); }
  }, [editItem]);

  useEffect(() => {
    const fn = e => { if (ddRef.current && !ddRef.current.contains(e.target)) setDdOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  // Pick product from dropdown — mirrors QuotationApp's pick()
  const pick = (p) => {
    const first = p.profileThickness?.[0];
    setForm(f => ({
      ...f,
      productDbId: p._id,
      itemCode: p.itemCode,
      sectionName: p.sectionName,
      sectionImage: p.sectionImage || "",
      item: p.item,
      profileCode: p.profileCode || "",
      hsnCode: p.hsnCode,
      uom: p.uom,
      lengthInMtrs: String(p.lengthDefault ?? 5.8),
      // auto-select first thickness variant
      thkn: first ? first.thkn : "",
      weightPerMeter: first ? first.weightPerMeter : "",
    }));
    setSrch(p.sectionName);
    setDdOpen(false);
  };

  const hKey = (e) => {
    if (!ddOpen) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi(i => Math.min(i + 1, filt.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHi(i => Math.max(i - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); if (filt[hi]) pick(filt[hi]); }
    if (e.key === "Escape") setDdOpen(false);
  };

  const h = (f, v) => setForm(p => ({ ...p, [f]: v }));

  // When user picks a thickness variant from the select
  const onThknSelect = (e) => {
    const idx = parseInt(e.target.value, 10);
    const v = thkOptions[idx];
    if (!v) return;
    setForm(f => ({ ...f, thkn: v.thkn, weightPerMeter: v.weightPerMeter }));
  };

  const submit = () => {
    if (!form.sectionName) { toast("Select a product section", "err"); return; }
    if (!form.thkn) { toast("Select a thickness variant", "err"); return; }
    if (!form.lengthInMtrs) { toast("Enter Length in MTRs", "err"); return; }
    if (!form.quantityInNumbers) { toast("Enter Quantity in NOS", "err"); return; }
    if (!form.pricePerKg) { toast("Enter Price per KG", "err"); return; }
    const row = compute({ ...form, _id: editItem ? editItem._id : Date.now() });
    if (editItem) { onUpdate(row); toast("Item updated"); }
    else { onAdd(row); toast("Item added"); }
    setForm(EMPTY_FORM); setSrch("");
  };

  const monoInput = (field, placeholder = "", type = "text", extra = {}) => (
    <input
      className={INPUT_CLS}
      style={{ fontFamily: "'Poppins', sans-serif" }}
      type={type}
      value={form[field]}
      onChange={e => h(field, e.target.value)}
      placeholder={placeholder}
      {...extra}
    />
  );

  return (
    <Panel>
      <PanelHead
        left={editItem ? <><span className="text-[#f0c040]">EDITING</span> — {editItem.itemCode}</> : "ADD ITEM TO PI"}
        right={
          <button
            className={`${BTN_BASE} ${BTN_OUT}`}
            style={{ fontFamily: "'Poppins',sans-serif", padding: "5px 13px", fontSize: 11 }}
            onClick={onBack}
          >← PI Details</button>
        }
      />
      <div className="px-[22px] py-5">

        {/* ── Search / Dropdown — identical to QuotationApp ── */}
        <div className="relative mb-4" ref={ddRef}>
          <FGrp label="Section Name" required>
            <input
              className={INPUT_CLS}
              style={{ fontFamily: "'Poppins', sans-serif" }}
              value={srch}
              onChange={e => { setSrch(e.target.value); setDdOpen(true); setHi(0); }}
              onFocus={() => setDdOpen(true)}
              onKeyDown={hKey}
              placeholder={catalogLoading ? "Loading products…" : "Search by section name, item code, dimensions or brand…"}
              disabled={catalogLoading}
            />
          </FGrp>
          {ddOpen && !catalogLoading && (
            <div className="absolute z-[300] left-0 right-0 top-[calc(100%+3px)] bg-[#eeeef6] border border-[#b8b8d4] rounded-lg max-h-[300px] overflow-y-auto shadow-[0_16px_40px_rgba(0,0,0,0.6)]">
              {filt.length === 0
                ? <div className="p-3 text-center text-[11px] text-[#4a4a75]" style={{ fontFamily: "'Poppins', sans-serif" }}>No results</div>
                : filt.map((p, i) => (
                  <div
                    key={p._id}
                    onClick={() => pick(p)}
                    className={`flex items-center gap-[10px] px-3 py-[9px] cursor-pointer transition-colors border-b border-[#d0d0e8] last:border-b-0 ${i === hi ? "bg-[rgba(124,109,250,0.1)]" : "hover:bg-[rgba(124,109,250,0.1)]"}`}
                  >
                    <img
                      src={p.sectionImage}
                      alt=""
                      className="w-[38px] h-[38px] rounded-[5px] border border-[#d0d0e8] object-cover flex-shrink-0"
                      onError={e => e.target.src = "https://placehold.co/38x38/eeeef6/555?text=?"}
                    />
                    <div>
                      {/* Primary row — white, like QuotationApp */}
                      <div className="text-[12px] font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Poppins',sans-serif" }}>
                        {p.sectionName}
                        <span className="text-[#7c6dfa] text-[10px] ml-2">{p.itemCode}</span>
                        {p.brand && <span className="text-[#f0c040] text-[9px] ml-2 font-normal">{p.brand}</span>}
                      </div>
                      {/* Secondary row — muted, like QuotationApp */}
                      <div className="text-[10px] text-[#4a4a75]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        {p.item}
                        {" · "}
                        {p.profileThickness?.length ?? 0} thickness variant{p.profileThickness?.length !== 1 ? "s" : ""}
                        {" · default "}
                        {p.lengthDefault}m
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          )}
        </div>

        {/* ── Catalog fields — identical layout to QuotationApp ── */}
        <FGrid className="mb-3">
          <FGrp label="Item Code">
            <input className={INPUT_CLS} style={{ fontFamily: "'Poppins', sans-serif" }} value={form.itemCode} disabled />
          </FGrp>
          <FGrp label="Item / Dimensions">{monoInput("item", "e.g. 17 X 32 X 17")}</FGrp>
          <FGrp label="HSN Code">{monoInput("hsnCode")}</FGrp>
          <FGrp label="UOM">
            <select className={INPUT_CLS} style={{ fontFamily: "'Poppins', sans-serif", cursor: "pointer" }} value={form.uom} onChange={e => h("uom", e.target.value)}>
              <option>KGS</option><option>MTR</option><option>NOS</option>
            </select>
          </FGrp>
          {/* THKN — select from profileThickness variants when product chosen, else plain input */}
          <FGrp label="THKN (MM)" required>
            {thkOptions.length > 0 ? (
              <select
                className={INPUT_CLS}
                style={{ fontFamily: "'Poppins', sans-serif", cursor: "pointer" }}
                value={selectedThknIdx >= 0 ? selectedThknIdx : ""}
                onChange={onThknSelect}
              >
                <option value="">— select variant —</option>
                {thkOptions.map((t, i) => (
                  <option key={i} value={i}>{t.thkn} mm  ·  {t.weightPerMeter} kg/m</option>
                ))}
              </select>
            ) : (
              monoInput("thkn", "", "number", { step: "0.1" })
            )}
          </FGrp>
        </FGrid>

        {/* ── Input + computed — identical to QuotationApp ── */}
        <FGrid>
          <FGrp label="Length in MTRs" required>{monoInput("lengthInMtrs", "e.g. 5.8", "number", { step: "0.01" })}</FGrp>
          <FGrp label="Weight / Meter (kg)">{monoInput("weightPerMeter", "", "number", { step: "0.0001" })}</FGrp>
          <FGrp label="Wt per Length (kg)" auto><Computed>{live.weightPerLength || "—"}</Computed></FGrp>
          <FGrp label="Quantity in NOS" required>{monoInput("quantityInNumbers", "e.g. 300", "number")}</FGrp>
          <FGrp label="Quantity in Meters" auto><Computed>{live.quantityInMeters || "—"}</Computed></FGrp>
          <FGrp label="Total KGS" auto><Computed>{live.totalKgs || "—"}</Computed></FGrp>
          <FGrp label="Price per KG" required>{monoInput("pricePerKg", "e.g. 78", "number", { step: "0.01" })}</FGrp>
          <FGrp label="Amount" auto><ComputedGold>{live.amount ? fIN(live.amount) : "—"}</ComputedGold></FGrp>
        </FGrid>

        <div className="flex gap-[10px] mt-[18px] flex-wrap">
          <button className={`${BTN_BASE} ${BTN_PRI}`} style={{ fontFamily: "'Poppins',sans-serif" }} onClick={submit}>
            {editItem ? "UPDATE ITEM" : "+ ADD ITEM"}
          </button>
          <button className={`${BTN_BASE} ${BTN_OUT}`} style={{ fontFamily: "'Poppins',sans-serif" }} onClick={() => { setForm(EMPTY_FORM); setSrch(""); }}>
            CLEAR
          </button>
        </div>
      </div>
    </Panel>
  );
}

// ─── ROOT: PICreator ──────────────────────────────────────────────────────────
export default function PICreator({ client, onClose }) {
  const [step, setStep] = useState(0);
  const [biz, setBiz] = useState(() => ({
    enquiryRef: "PH. NO. 9713330178",
    piRefNo: generatePINumber(),
    date: new Date().toISOString().split("T")[0],
    profileName: "",
    preparedBy: COMPANY.preparedByDefault,
    authorizedBy: COMPANY.authorizedByDefault,
    materialSpec: "PRE-GALVANIZED STEEL CONFORMING TO IS277 MAX-120GSM",
    paymentTerms: "50% ADV. ALONG WITH ORDER CONFIRMATION AND BALANCE 50% AT ACTUALS AGAINST FINAL P.I. ON READINESS OF MATERIAL PRIOR TO DISPATCH.",
    deliveryTerms: "WE SHALL ABLE TO DISPATCH WITHIN 8-10 DAYS FROM THE DATE OF YOUR CONFIRMATION ON OUR P.I.",
    packing: "AS PER OUR STANDARDS",
    inspection: "IF REQD AT OUR WORKS BY YOUR REPRESENTATIVE PRIOR TO DISPATCH",
    loadingCharge: 500,
    // ↓ pre-filled from client prop
    companyName: client?.companyName || "",
    gstin: client?.gstin || "",
    address: client?.address || "",
    mobile: client?.mobile || "",
    email: client?.email || "",
    shipCompanyName: client?.shipCompanyName || "",
    shipPhone: client?.shipPhone || "",
    shipAddress: client?.shipAddress || "",
  }));
  const [items, setItems] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCL] = useState(true);
  const [saving, setSaving] = useState(false);
  const { list, toast } = useToast();
  const navigate = useNavigate();

  // Fetch products from API on mount
  useEffect(() => {
    (async () => {
      setCL(true);
      try {
        const res = await API.get(PRODUCTS_API);
        setCatalog(res.data.data || []);
      } catch (err) {
        toast("Failed to load products from API", "err");
        console.error("Fetch products failed:", err);
      } finally {
        setCL(false);
      }
    })();
  }, [toast]);

  // Escape key closes the overlay
  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  // Totals — same as QuotationApp
  const totals = useMemo(() => items.reduce(
    (a, r) => ({ kg: a.kg + r.totalKgs, mtr: a.mtr + r.quantityInMeters, amt: a.amt + r.amount }),
    { kg: 0, mtr: 0, amt: 0 }
  ), [items]);

  const loadingC = parseFloat(biz.loadingCharge) || 0;

  // Item CRUD — same as QuotationApp (duplicate guard: same itemCode + thkn)
  const addItem = (row) => {
    if (items.find(i => i.itemCode === row.itemCode && String(i.thkn) === String(row.thkn))) {
      toast("Same item + thickness already in list", "err"); return;
    }
    setItems(p => [...p, row]);
  };
  const updItem = (row) => { setItems(p => p.map(i => i._id === row._id ? row : i)); setEditItem(null); };
  const delItem = (id) => { setItems(p => p.filter(i => i._id !== id)); if (editItem?._id === id) setEditItem(null); toast("Item removed"); };
  const startEdit = (item) => { setEditItem(item); };

  const saveToDB = async () => {
    if (!biz.companyName || !biz.piRefNo) { toast("Company Name & PI Ref No. required", "err"); return; }
    if (!items.length)                    { toast("Add at least one item before saving", "err"); return; }
    setSaving(true);
    try {
      const cleanItems = items.map(({ _id, ...rest }) => rest);
      const res = await API.post(PI_API, {
        ...biz,
        loadingCharge: parseFloat(biz.loadingCharge) || 0,
        items:         cleanItems,
        clientId:      client?._id || null,
      });
      toast("PI saved to database ✓ — redirecting…");
      setTimeout(() => navigate(`/pi/${res.data.data._id}`), 900);
    } catch (err) {
      toast(err.response?.data?.message || "Save failed", "err");
    } finally {
      setSaving(false);
    }
  };

  // Step circle style — exact copy from QuotationApp
  const stepCls = (active, done) => [
    "w-[27px] h-[27px] rounded-full border-[1.5px] flex items-center justify-center text-[11px] transition-all duration-200",
    done ? "border-[#40d098] text-[#40d098] bg-[rgba(64,208,152,0.1)]" : "",
    active ? "border-[#7c6dfa] text-[#7c6dfa] bg-[rgba(124,109,250,0.12)]" : "",
    !active && !done ? "border-[#b8b8d4] text-[#4a4a75]" : "",
  ].join(" ");

  // Summary items — exact copy from QuotationApp
  const SUMMARY_ITEMS = [
    { label: "Items", val: items.length, cls: "text-[#7c6dfa]", sz: 19 },
    { label: "Total KGS", val: f2(totals.kg), cls: "text-[#40d098]", sz: 19 },
    { label: "Total Meters", val: f2(totals.mtr), cls: "text-[#40c0f0]", sz: 19 },
    { label: "Assessable Value", val: fIN(totals.amt), cls: "text-[#f0c040]", sz: 19 },
    { label: "Loading", val: fIN(loadingC), cls: "text-[#1a1a2e]", sz: 15 },
    { label: `IGST ${COMPANY.gstRate * 100}%`, val: fIN((totals.amt + loadingC) * COMPANY.gstRate), cls: "text-[#1a1a2e]", sz: 15 },
    { label: "Grand Total", val: fIN((totals.amt + loadingC) * (1 + COMPANY.gstRate)), cls: "text-[#f0c040]", sz: 22 },
  ];

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{MINIMAL_CSS}</style>

      {/* Full-screen overlay — same bg + radial gradient as QuotationApp page */}
      <div
        className=" bg-[#ffffff]"
        style={{ backgroundImage: "radial-gradient(ellipse 60% 28% at 50% -4%,rgba(124,109,250,.09),transparent 55%)" }}
      >

        {/* ── Top bar with stepper (mirrors QuotationApp's stepper strip) ── */}
        {/* ── Top bar with stepper ── */}
        <div className="flex-shrink-0 border-b border-[#d0d0e8] bg-[#f8f8ff]">  {/* ← was bg-[#0d0d18] */}
          <div className="flex items-center justify-between max-w-[1540px] mx-auto px-6 py-3">

            {/* Stepper */}
            <div className="flex items-center">

              {/* Step 1 */}
              <div className="flex items-center cursor-pointer" onClick={() => setStep(0)}>
                <div className={stepCls(step === 0, step > 0)} style={{ fontFamily: "'Poppins', sans-serif" }}>{step > 0 ? "✓" : "1"}</div>
                <span className={`text-[12px] font-semibold tracking-[1px] ml-2 ${step === 0 ? "text-[#1a1a2e]" : "text-[#4a4a75]"}`} style={{ fontFamily: "'Poppins',sans-serif" }}>PI DETAILS</span>
              </div>

              <div className="w-[34px] h-px bg-[#d0d0e8] mx-[10px]" />

              {/* Step 2 */}
              <div className={`flex items-center ${step > 0 ? "cursor-pointer" : "cursor-default"}`} onClick={() => step > 0 && setStep(1)}>
                <div className={stepCls(step === 1, false)} style={{ fontFamily: "'Poppins', sans-serif" }}>2</div>
                <span className={`text-[12px] font-semibold tracking-[1px] ml-2 ${step === 1 ? "text-[#1a1a2e]" : "text-[#4a4a75]"}`} style={{ fontFamily: "'Poppins',sans-serif" }}>ITEMS</span>
              </div>

              <div className="w-[34px] h-px bg-[#d0d0e8] mx-[10px]" />

              {/* Step 3 */}
              <div
                className={`flex items-center ${items.length > 0 ? "cursor-pointer" : "cursor-default"}`}
                onClick={() => items.length > 0 && generatePDF(biz, items, toast)}
              >
                <div className={stepCls(false, false)} style={{ fontFamily: "'Poppins', sans-serif" }}>3</div>
                <span className="text-[12px] font-semibold tracking-[1px] ml-2 text-[#4a4a75]" style={{ fontFamily: "'Poppins',sans-serif" }}>GENERATE PDF</span>
              </div>

              {/* Client pill */}
              {client && (
                <>
                  <div className="w-[34px] h-px bg-[#d0d0e8] mx-[10px]" />
                  <span className="text-[10px] text-[#1a9ec0] bg-[rgba(64,192,240,0.08)] border border-[rgba(64,192,240,0.22)] px-[10px] py-[3px] rounded-full" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {client.companyName}
                  </span>
                </>
              )}

              {/* Catalog loading spinner */}
              {catalogLoading && (
                <span className="ml-4 flex items-center gap-[6px] text-[10px] text-[#4a4a75]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  <span className="anim-spin inline-block w-3 h-3 rounded-full border-2 border-[#b8b8d4] border-t-[#7c6dfa]" />
                  loading products…
                </span>
              )}
            </div>

            {/* Close button */}
            {/* <button
              className={`${BTN_BASE} ${BTN_OUT}`}
              style={{ fontFamily: "'Poppins',sans-serif", padding: "5px 14px", fontSize: 12 }}
              onClick={onClose}
            >✕ CLOSE</button> */}
          </div>
        </div>

        {/* ── Scrollable content area — same max-width + padding as QuotationApp ── */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-[1540px] mx-auto px-6 pt-[22px] pb-20">

            {/* ── Step 0: Biz / Client form ── */}
            {step === 0 && (
              <BizForm
                biz={biz}
                setBiz={setBiz}
                client={client}
                onNext={() => {
                  if (!biz.companyName || !biz.piRefNo) { toast("Company Name & PI Ref No. required", "err"); return; }
                  setStep(1);
                }}
              />
            )}

            {/* ── Step 1: Item form + summary + table ── */}
            {step === 1 && (
              <>
                <ItemForm
                  editItem={editItem}
                  catalog={catalog}
                  catalogLoading={catalogLoading}
                  onAdd={addItem}
                  onUpdate={updItem}
                  onBack={() => setStep(0)}
                  toast={toast}
                />

                {/* ── Summary bar — exact copy from QuotationApp ── */}
                {items.length > 0 && (
                  <div className="bg-[#eeeef6] border border-[#d0d0e8] rounded-xl px-[22px] py-[15px] flex flex-wrap items-center gap-[22px] mb-[22px]">
                    {SUMMARY_ITEMS.map((s, i) => (
                      <span key={s.label} className="flex items-center gap-[22px]">
                        <span className="flex flex-col gap-[2px]">
                          <span className="text-[9px] tracking-[2px] uppercase text-[#4a4a75]" style={{ fontFamily: "'Poppins', sans-serif" }}>{s.label}</span>
                          <span className={`font-bold ${s.cls}`} style={{ fontFamily: "'Poppins',sans-serif", fontSize: s.sz }}>{s.val}</span>
                        </span>
                        {i < SUMMARY_ITEMS.length - 1 && <span className="w-px h-8 bg-[#d0d0e8] hidden md:block" />}
                      </span>
                    ))}
                    <div className="ml-auto flex gap-[10px] flex-wrap">
                      <button
                        className={`${BTN_BASE} ${BTN_GREEN}`}
                        style={{ fontFamily: "'Poppins',sans-serif", opacity: saving ? 0.65 : 1 }}
                        onClick={saveToDB}
                        disabled={saving}
                      >
                        {saving
                          ? <><span className="anim-spin inline-block w-[13px] h-[13px] rounded-full border-2 border-[#ffffff] border-t-transparent" />SAVING…</>
                          : <>💾 SAVE TO DB</>}
                      </button>
                      <button className={`${BTN_BASE} ${BTN_AMBER}`} style={{ fontFamily: "'Poppins',sans-serif" }} onClick={() => generatePDF(biz, items, toast)}>↓ GENERATE PDF</button>
                      <button className={`${BTN_BASE} ${BTN_OUT}`} style={{ fontFamily: "'Poppins',sans-serif" }} onClick={() => { if (window.confirm("Reset all items?")) { setItems([]); setEditItem(null); toast("Cleared"); } }}>RESET</button>
                    </div>
                  </div>
                )}

                {/* ── Items table — exact copy from QuotationApp ── */}
                <Panel>
                  <PanelHead
                    left={`PI ITEMS — ${biz.piRefNo || "…"}`}
                    right={
                      <span className="text-[11px] text-[#4a4a75]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                        <span className="text-[#7c6dfa] font-bold">{items.length}</span> item{items.length !== 1 ? "s" : ""}
                      </span>
                    }
                  />
                  <div className="overflow-x-auto">
                    {items.length === 0 ? (
                      <div className="text-center py-[50px] px-5">
                        <div className="text-[38px] opacity-20 mb-[10px]">📋</div>
                        <div className="text-[15px] font-semibold opacity-30 mb-1 text-[#1a1a2e]" style={{ fontFamily: "'Poppins',sans-serif" }}>No items yet</div>
                        <div className="text-[12px] text-[#4a4a75]">Use the form above to add sections</div>
                      </div>
                    ) : (
                      <table className="w-full border-collapse">
                        <thead className="bg-[#eeeef6]">
                          <tr>
                            {["S.NO.", "Item Code", "Section Name"].map(hd => (
                              <th key={hd} className="text-[9px] tracking-[1.2px] uppercase text-[#4a4a75] px-[9px] py-[10px] text-left whitespace-nowrap border-b border-[#d0d0e8]" style={{ fontFamily: "'Poppins', sans-serif" }}>{hd}</th>
                            ))}
                            <th className="text-[9px] tracking-[1.2px] uppercase text-[#4a4a75] px-[9px] py-[10px] text-center whitespace-nowrap border-b border-[#d0d0e8]" style={{ fontFamily: "'Poppins', sans-serif" }}>Drawing</th>
                            <th className="text-[9px] tracking-[1.2px] uppercase text-[#4a4a75] px-[9px] py-[10px] text-left whitespace-nowrap border-b border-[#d0d0e8]" style={{ fontFamily: "'Poppins', sans-serif" }}>Item</th>
                            <th className="text-[9px] tracking-[1.2px] uppercase text-[#4a4a75] px-[9px] py-[10px] text-left whitespace-nowrap border-b border-[#d0d0e8]" style={{ fontFamily: "'Poppins', sans-serif" }}>HSN</th>
                            <th className="text-[9px] tracking-[1.2px] uppercase text-[#4a4a75] px-[9px] py-[10px] text-center whitespace-nowrap border-b border-[#d0d0e8]" style={{ fontFamily: "'Poppins', sans-serif" }}>UOM</th>
                            {["THKN(mm)", "Length(Mtr)", "Wt/Mtr", "Wt/Len", "IN NO.", "IN MTR", "Total KGS", "Price/KG", "Amount"].map(hd => (
                              <th key={hd} className="text-[9px] tracking-[1.2px] uppercase text-[#4a4a75] px-[9px] py-[10px] text-right whitespace-nowrap border-b border-[#d0d0e8]" style={{ fontFamily: "'Poppins', sans-serif" }}>{hd}</th>
                            ))}
                            <th className="text-[9px] tracking-[1.2px] uppercase text-[#4a4a75] px-[9px] py-[10px] text-center whitespace-nowrap border-b border-[#d0d0e8]" style={{ fontFamily: "'Poppins', sans-serif" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((r, i) => (
                            <tr
                              key={r._id}
                              className={`border-b border-[rgba(208,208,232,0.7)] transition-colors hover:bg-[rgba(124,109,250,0.04)] ${editItem?._id === r._id ? "bg-[rgba(124,109,250,0.07)]" : ""}`}
                            >
                              <td className="px-[9px] py-[9px] text-[11px] text-[#4a4a75]" style={{ fontFamily: "'Poppins', sans-serif" }}>{i + 1}</td>
                              <td className="px-[9px] py-[9px] text-[11px] text-[#7c6dfa] font-medium" style={{ fontFamily: "'Poppins', sans-serif" }}>{r.itemCode}</td>
                              <td className="px-[9px] py-[9px]"><div className="text-[11px] font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Poppins',sans-serif" }}>{r.sectionName}</div></td>
                              <td className="px-[9px] py-[9px] text-center">
                                <img src={r.sectionImage} alt="" className="w-[34px] h-[34px] rounded-[5px] border border-[#d0d0e8] object-cover inline-block" onError={e => e.target.src = "https://placehold.co/34x34/eeeef6/555?text=?"} />
                              </td>
                              <td className="px-[9px] py-[9px]"><div className="text-[10px] text-[#4a4a75]" style={{ fontFamily: "'Poppins', sans-serif" }}>{r.item}</div></td>
                              <td className="px-[9px] py-[9px] text-[11px] text-[#4a4a75]" style={{ fontFamily: "'Poppins', sans-serif" }}>{r.hsnCode}</td>
                              <td className="px-[9px] py-[9px] text-center">
                                <span className="inline-block px-[6px] py-[1px] rounded-[3px] text-[9px] tracking-[1px] bg-[rgba(64,192,240,0.1)] text-[#40c0f0] border border-[rgba(64,192,240,0.2)]" style={{ fontFamily: "'Poppins', sans-serif" }}>{r.uom}</span>
                              </td>
                              {/* plain numeric — primary white */}
                              {[r.thkn, r.lengthInMtrs, r.weightPerMeter].map((v, vi) => (
                                <td key={vi} className="px-[9px] py-[9px] text-[11px] text-[#1a1a2e] text-right whitespace-nowrap" style={{ fontFamily: "'Poppins', sans-serif" }}>{v}</td>
                              ))}
                              {/* computed — green */}
                              {[f3(r.weightPerLength), r.quantityInNumbers, f2(r.quantityInMeters), f2(r.totalKgs)].map((v, vi) => (
                                <td key={vi} className="px-[9px] py-[9px] text-[11px] text-[#40d098] text-right whitespace-nowrap" style={{ fontFamily: "'Poppins', sans-serif" }}>{v}</td>
                              ))}
                              <td className="px-[9px] py-[9px] text-[11px] text-[#1a1a2e] text-right whitespace-nowrap" style={{ fontFamily: "'Poppins', sans-serif" }}>{f2(r.pricePerKg)}</td>
                              <td className="px-[9px] py-[9px] text-[12px] font-bold text-[#f0c040] text-right whitespace-nowrap" style={{ fontFamily: "'Poppins', sans-serif" }}>{fIN(r.amount)}</td>
                              <td className="px-[9px] py-[9px] text-center">
                                <div className="flex gap-1 justify-center">
                                  <button className={BTN_EDIT} style={{ fontFamily: "'Poppins',sans-serif" }} onClick={() => startEdit(r)}>Edit</button>
                                  <button className={BTN_DEL} style={{ fontFamily: "'Poppins',sans-serif" }} onClick={() => delItem(r._id)}>Del</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {/* Total footer row — exact copy from QuotationApp */}
                          <tr className="bg-[#eeeef6]" style={{ borderTop: "1px solid #b8b8d4" }}>
                            <td colSpan={13} className="px-[9px] py-[9px] text-[11px] font-bold text-[#4a4a75] tracking-[1px] text-right pr-2" style={{ fontFamily: "'Poppins',sans-serif" }}>TOTAL KGS →</td>
                            <td className="px-[9px] py-[9px] text-[11px] font-bold text-[#40d098] text-right" style={{ fontFamily: "'Poppins', sans-serif" }}>{f2(totals.kg)}</td>
                            <td />
                            <td className="px-[9px] py-[9px] text-[12px] font-bold text-[#f0c040] text-right" style={{ fontFamily: "'Poppins', sans-serif" }}>{fIN(totals.amt)}</td>
                            <td />
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                </Panel>
              </>
            )}

          </div>
        </div>
      </div>

      {/* ── Toasts — exact copy from QuotationApp ── */}
      <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[999] flex flex-col gap-[6px] items-center pointer-events-none">
        {list.map(t => (
          <div
            key={t.id}
            className={[
              "anim-tup bg-[#eeeef6] border rounded-[7px] px-5 py-[10px] text-[12px] whitespace-nowrap shadow-[0_8px_24px_rgba(0,0,0,0.45)]",
              t.type === "ok" ? "border-[rgba(64,208,152,0.4)] text-[#40d098]" : "",
              t.type === "err" ? "border-[rgba(240,80,96,0.4)]  text-[#f05060]" : "",
              t.type !== "ok" && t.type !== "err" ? "border-[#b8b8d4] text-[#1a1a2e]" : "",
            ].join(" ")}
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {t.msg}
          </div>
        ))}
      </div>
    </>
  );
}